import type { ReactNode } from "react";

/**
 * Единый набор иконок (docs/design/README.md): контур 24×24, линия 1.75, скруглённые концы, цвет от текста
 * (`currentColor`). Без библиотек иконок и без символов-заменителей («›», «×», «↑») в интерфейсе.
 * Размер задаётся классом (`h-5 w-5`), по умолчанию 24px.
 */
function Icon({ children, className = "h-6 w-6" }: { children: ReactNode; className?: string | undefined }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      {children}
    </svg>
  );
}

type IconProps = { className?: string };

/* Навигация кабинета */

export const CalendarIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
    <path d="M3.5 10h17M8 3v4M16 3v4" />
    <path d="M8 14h.01M12 14h.01M16 14h.01M8 17.5h.01M12 17.5h.01" strokeWidth={2.25} />
  </Icon>
);

export const ChatIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="M20.5 11.5c0 4.1-3.8 7.5-8.5 7.5-1.2 0-2.4-.2-3.4-.6L4 20l1.3-3.6c-1.1-1.3-1.8-3-1.8-4.9C3.5 7.4 7.3 4 12 4s8.5 3.4 8.5 7.5Z" />
    <path d="M8.5 11.5h.01M12 11.5h.01M15.5 11.5h.01" strokeWidth={2.25} />
  </Icon>
);

export const ClientsIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <circle cx="9.5" cy="8.5" r="3.5" />
    <path d="M3 19.5c.6-3.2 3.3-5.5 6.5-5.5s5.9 2.3 6.5 5.5" />
    <path d="M15.5 5.2a3.5 3.5 0 0 1 0 6.6M18 14.5c1.6.8 2.7 2.4 3 5" />
  </Icon>
);

export const BellIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="M6 16.5V11a6 6 0 0 1 12 0v5.5l1.5 2h-15l1.5-2Z" />
    <path d="M10 21h4" />
  </Icon>
);

export const MenuIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="M4 6.5h16M4 12h16M4 17.5h16" />
  </Icon>
);

export const SettingsIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <circle cx="12" cy="12" r="3" />
    <path d="M12 3.5v2M12 18.5v2M3.5 12h2M18.5 12h2M6 6l1.4 1.4M16.6 16.6 18 18M6 18l1.4-1.4M16.6 7.4 18 6" />
  </Icon>
);

export const BillingIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <rect x="3" y="5.5" width="18" height="13" rx="2.5" />
    <path d="M3 10h18M7 15h3" />
  </Icon>
);

export const ReportsIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="M4 20h16" />
    <path d="M7 16v-5M12 16V7M17 16v-3" strokeWidth={2.25} />
  </Icon>
);

export const LogoutIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="M14 4.5H7.5a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2H14" />
    <path d="M10.5 12h10M17 8.5l3.5 3.5-3.5 3.5" />
  </Icon>
);

/* Действия */

export const PlusIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="M12 5v14M5 12h14" />
  </Icon>
);

export const CheckIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="m5 12.5 4.5 4.5L19 7.5" />
  </Icon>
);

export const CloseIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="M6 6l12 12M18 6 6 18" />
  </Icon>
);

export const SearchIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <circle cx="11" cy="11" r="6.5" />
    <path d="m16 16 4.5 4.5" />
  </Icon>
);

export const CopyIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <rect x="8.5" y="8.5" width="12" height="12" rx="2.5" />
    <path d="M15.5 8.5V6a2.5 2.5 0 0 0-2.5-2.5H6A2.5 2.5 0 0 0 3.5 6v7A2.5 2.5 0 0 0 6 15.5h2.5" />
  </Icon>
);

export const EditIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="M4 20h4l10.5-10.5a2.1 2.1 0 0 0 0-3L17 5a2.1 2.1 0 0 0-3 0L3.5 15.5V20Z" />
    <path d="m12.5 6.5 5 5" />
  </Icon>
);

export const TrashIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="M4.5 7h15M9.5 7V4.5h5V7M6.5 7l.8 12a1.5 1.5 0 0 0 1.5 1.4h6.4a1.5 1.5 0 0 0 1.5-1.4l.8-12" />
    <path d="M10 11v6M14 11v6" />
  </Icon>
);

export const RefreshIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="M20 12a8 8 0 0 1-13.9 5.4M4 12a8 8 0 0 1 13.9-5.4" />
    <path d="M18 3v4h-4M6 21v-4h4" />
  </Icon>
);

export const DownloadIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="M12 4v11M7.5 10.5 12 15l4.5-4.5" />
    <path d="M4.5 16.5v1.5a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2v-1.5" />
  </Icon>
);

export const ExternalLinkIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="M14 4.5h5.5V10M19.5 4.5 11 13" />
    <path d="M19.5 14v3.5a2 2 0 0 1-2 2h-11a2 2 0 0 1-2-2v-11a2 2 0 0 1 2-2H10" />
  </Icon>
);

export const PauseIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="M8.5 5.5v13M15.5 5.5v13" strokeWidth={2.25} />
  </Icon>
);

export const PlayIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="M7 5.5v13l11-6.5L7 5.5Z" />
  </Icon>
);

export const SendIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="M4.5 12 20 4.5 16 20l-4-6.5-7.5-1.5Z" />
    <path d="M12 13.5 20 4.5" />
  </Icon>
);

export const MicIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <rect x="9" y="3.5" width="6" height="11" rx="3" />
    <path d="M5.5 11.5a6.5 6.5 0 0 0 13 0M12 18v2.5M9 20.5h6" />
  </Icon>
);

export const CameraIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="M4 8.5A1.5 1.5 0 0 1 5.5 7h2.3l1.4-2h5.6l1.4 2h2.3A1.5 1.5 0 0 1 20 8.5v9a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 17.5v-9Z" />
    <circle cx="12" cy="13" r="3.25" />
  </Icon>
);

export const FileIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="M6 3.5h8l4.5 4.5v12.5h-12.5V3.5Z" />
    <path d="M14 3.5V8h4.5M9 13h6M9 16.5h6" />
  </Icon>
);

/* Стрелки */

export const ChevronLeftIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="m14.5 6-6 6 6 6" />
  </Icon>
);

export const ChevronRightIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="m9.5 6 6 6-6 6" />
  </Icon>
);

export const ChevronDownIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="m6 9.5 6 6 6-6" />
  </Icon>
);

export const ChevronUpIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="m6 14.5 6-6 6 6" />
  </Icon>
);

export const ArrowUpIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="M12 19V5M6 11l6-6 6 6" />
  </Icon>
);

export const ArrowDownIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="M12 5v14M6 13l6 6 6-6" />
  </Icon>
);

export const ArrowRightIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="M5 12h14M13 6l6 6-6 6" />
  </Icon>
);

/* Сущности */

export const ClockIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.5V12l3 2" />
  </Icon>
);

export const UserIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <circle cx="12" cy="8.5" r="3.75" />
    <path d="M5 20c.7-3.5 3.6-6 7-6s6.3 2.5 7 6" />
  </Icon>
);

export const PhoneIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="M6.5 3.5h3l1.5 4-2 1.5a11 11 0 0 0 6 6l1.5-2 4 1.5v3a2 2 0 0 1-2 2A15.5 15.5 0 0 1 4.5 5.5a2 2 0 0 1 2-2Z" />
  </Icon>
);

export const MailIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <rect x="3.5" y="5.5" width="17" height="13" rx="2.5" />
    <path d="m4 7 8 6 8-6" />
  </Icon>
);

export const InstagramIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <rect x="3.5" y="3.5" width="17" height="17" rx="5" />
    <circle cx="12" cy="12" r="4" />
    <path d="M17 7h.01" strokeWidth={2.5} />
  </Icon>
);

export const QrIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <rect x="4" y="4" width="6.5" height="6.5" rx="1.5" />
    <rect x="13.5" y="4" width="6.5" height="6.5" rx="1.5" />
    <rect x="4" y="13.5" width="6.5" height="6.5" rx="1.5" />
    <path d="M13.5 13.5h3v3h-3zM20 13.5v3M16.5 20H20M13.5 20h.01" />
  </Icon>
);

export const LinkIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1" />
    <path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" />
  </Icon>
);

export const SparkleIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="M12 3.5 14 9l5.5 2L14 13l-2 5.5L10 13l-5.5-2L10 9l2-5.5Z" />
    <path d="M19 17.5v3M17.5 19h3" />
  </Icon>
);

export const ScissorsIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <circle cx="6.5" cy="6.5" r="2.75" />
    <circle cx="6.5" cy="17.5" r="2.75" />
    <path d="M8.7 8.3 20 20M8.7 15.7 20 4" />
  </Icon>
);

export const TeamIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <circle cx="12" cy="7.5" r="3" />
    <path d="M6.5 19c.5-3 2.8-5 5.5-5s5 2 5.5 5" />
    <path d="M5 9.5a2.3 2.3 0 1 0 0-4.6M19 9.5a2.3 2.3 0 1 1 0-4.6M2.5 17c.3-2 1.6-3.5 3.5-3.9M21.5 17c-.3-2-1.6-3.5-3.5-3.9" />
  </Icon>
);

export const ShieldIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="M12 3.5 5 6v5.5c0 4.2 2.9 7.6 7 9 4.1-1.4 7-4.8 7-9V6l-7-2.5Z" />
    <path d="m9.5 12 2 2 3.5-4" />
  </Icon>
);

export const WalletIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="M4 7.5A2.5 2.5 0 0 1 6.5 5h11v3" />
    <rect x="4" y="8" width="16.5" height="11.5" rx="2.5" />
    <path d="M15.5 14h2" strokeWidth={2.25} />
  </Icon>
);

export const HomeIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="m4 11 8-7 8 7v8.5a1.5 1.5 0 0 1-1.5 1.5H15v-6H9v6H5.5A1.5 1.5 0 0 1 4 19.5V11Z" />
  </Icon>
);

/* Состояния */

export const InfoIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 11v5.5M12 7.75h.01" strokeWidth={2.25} />
  </Icon>
);

export const AlertIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="M12 4 2.75 19.5h18.5L12 4Z" />
    <path d="M12 10v4M12 17h.01" strokeWidth={2.25} />
  </Icon>
);

export const ErrorIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M9 9l6 6M15 9l-6 6" />
  </Icon>
);

export const SuccessIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="m8.5 12.5 2.5 2.5 4.5-5" />
  </Icon>
);

export const InboxIcon = ({ className }: IconProps) => (
  <Icon className={className}>
    <path d="M4 13.5 6.3 5.5h11.4L20 13.5v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-4Z" />
    <path d="M4 13.5h4.5l1.5 2.5h4l1.5-2.5H20" />
  </Icon>
);

/** Знак продукта (тот же рисунок, что у фавикона и иконки PWA): пузырёк переписки с галочкой на фоне бренда. */
export const BrandMark = ({ className = "h-8 w-8" }: IconProps) => (
  <svg viewBox="0 0 64 64" className={className} aria-hidden="true">
    <rect width="64" height="64" rx="14" fill="var(--color-brand)" />
    <path
      d="M32 13.5c-10.8 0-19.5 7.4-19.5 16.5 0 4.5 2.1 8.6 5.6 11.6L15.5 51l11.2-4.3c1.7.4 3.5.6 5.3.6 10.8 0 19.5-7.4 19.5-16.5S42.8 13.5 32 13.5z"
      fill="#ffffff"
    />
    <path d="M24 30.5l5.5 5.5L40.5 25" fill="none" stroke="var(--color-brand)" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
